import { DayOfWeekApiType, ServiceTimeSlotApiDto, ShopApiDto } from '@b2x/storefront-api-js-client/src/dto';
import { Helmet } from 'react-helmet-async';
import { helmetJsonLdProp } from 'react-schemaorg';
import {
  DayOfWeek,
  EntertainmentBusiness as EntertainmentBusinessSchema,
  Graph,
  LocalBusiness,
  OpeningHoursSpecification,
  PostalAddress,
  WithContext,
} from 'schema-dts';

import { appConfig } from '../config';
import { useCanonicalUrl, useLanguageTag } from './hooks';
import type { JsonLdBrand } from './types';
import { jsonLdScriptProp } from './util';
import { buildWebPageNode, getWebPageId } from './WebPage';

export interface EntertainmentBusinessGraphInput {
  /** Brand del negozio; se assente il grafo esce senza brand, about e logo. */
  brand?: JsonLdBrand;
  /** Lingua della pagina come tag BCP 47 (es. "it-IT"), per l'inLanguage del WebPage. */
  inLanguage?: string;
  /** Nome del negozio da esporre, già risolto (es. tramite shopNameResolver). */
  name?: string;
  /** Titolo della pagina del negozio (metadati bcom). */
  pageTitle?: string;
  shop: ShopApiDto;
  /** URL canonico della pagina: origin + pathname, senza query string e senza slash finale. */
  url: string;
}

const schemaOrgDayOfWeekByApiDay: Record<DayOfWeekApiType, DayOfWeek> = {
  FRIDAY: 'https://schema.org/Friday',
  MONDAY: 'https://schema.org/Monday',
  SATURDAY: 'https://schema.org/Saturday',
  SUNDAY: 'https://schema.org/Sunday',
  THURSDAY: 'https://schema.org/Thursday',
  TUESDAY: 'https://schema.org/Tuesday',
  WEDNESDAY: 'https://schema.org/Wednesday',
};

// Slot segnaposto che l'API restituisce anche per i giorni aperti: non è un'apertura reale da mezzanotte a mezzanotte.
const isPlaceholderTimeSlot = (timeSlot: ServiceTimeSlotApiDto) =>
  timeSlot.timeFrom === '00:00' && timeSlot.timeTo === '00:00';

/*
  Una voce per ogni coppia giorno e fascia oraria di timeRangeByDays.
  Scarta i giorni chiusi e gli slot 00:00-00:00 che l'API restituisce anche per i giorni aperti.
*/
const buildOpeningHoursSpecification = (shop: ShopApiDto): Array<OpeningHoursSpecification> | undefined => {
  const specifications = (shop.timeRangeByDays ?? []).flatMap((day) => {
    const dayOfWeek = day.dayOfWeek;
    if (dayOfWeek === undefined || day.state === 'CLOSED') {
      return [];
    }
    return (day.timeSlots ?? [])
      .filter((timeSlot) => timeSlot.timeFrom && timeSlot.timeTo && !isPlaceholderTimeSlot(timeSlot))
      .map((timeSlot): OpeningHoursSpecification => ({
        '@type': 'OpeningHoursSpecification',
        closes: timeSlot.timeTo,
        dayOfWeek: schemaOrgDayOfWeekByApiDay[dayOfWeek],
        opens: timeSlot.timeFrom,
      }));
  });
  return specifications.length > 0 ? specifications : undefined;
};

/*
  Coordinate del negozio, solo se presenti entrambe: senza, geo e hasMap vengono omessi.
  Il confronto con null copre anche un eventuale null nel JSON dell'API, non escluso a runtime dal tipo.
*/
const getCoordinates = (shop: ShopApiDto): { latitude: number; longitude: number } | undefined => {
  const latitude = shop.address?.latitude;
  const longitude = shop.address?.longitude;
  return latitude != null && longitude != null ? { latitude: latitude, longitude: longitude } : undefined;
};

const buildPostalAddress = (shop: ShopApiDto): PostalAddress => ({
  '@type': 'PostalAddress',
  addressCountry: shop.address?.country?.code,
  addressLocality: shop.address?.city,
  addressRegion: shop.address?.province?.code,
  postalCode: shop.address?.zipCode,
  streetAddress: shop.address?.addressLine1,
});

// Immagine principale seguita dalle alternative; undefined se il negozio non ne ha.
const getShopImageUrls = (shop: ShopApiDto): Array<string> | undefined => {
  const images = [...(shop.image ? [shop.image] : []), ...(shop.alternativeImages ?? [])];
  return images.length > 0 ? images.map((image) => image.src) : undefined;
};

const buildHasMap = (coordinates: { latitude: number; longitude: number }): string =>
  `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`;

/*
  Costruisce il grafo JSON-LD della scheda negozio: WebPage + EntertainmentBusiness collegati tramite @id.
  Funzione pura, così l'output è verificabile senza montare lo store locator.
*/
export const buildEntertainmentBusinessGraph = ({
  brand,
  inLanguage,
  name,
  pageTitle,
  shop,
  url,
}: EntertainmentBusinessGraphInput): Graph => {
  const localBusinessId = `${url}#localbusiness`;
  const brandReference = brand ? { '@id': brand.id } : undefined;
  const coordinates = getCoordinates(shop);
  // Su bcom alcuni negozi hanno il telefono composto da soli spazi: viene trattato come assente, altrimenti esce così com'è.
  const telephone = shop.phoneNumber?.trim() ? shop.phoneNumber : undefined;
  const webPage = buildWebPageNode({
    brand: brand,
    inLanguage: inLanguage,
    mainEntityId: localBusinessId,
    name: pageTitle,
    url: url,
  });
  const entertainmentBusiness: EntertainmentBusinessSchema = {
    '@id': localBusinessId,
    '@type': 'EntertainmentBusiness',
    address: buildPostalAddress(shop),
    brand: brandReference,
    geo: coordinates
      ? { '@type': 'GeoCoordinates', latitude: coordinates.latitude, longitude: coordinates.longitude }
      : undefined,
    hasMap: coordinates ? buildHasMap(coordinates) : undefined,
    image: getShopImageUrls(shop),
    logo: brand?.logoUrl ? { '@type': 'ImageObject', url: brand.logoUrl } : undefined,
    mainEntityOfPage: { '@id': getWebPageId(url) },
    name: name,
    openingHoursSpecification: buildOpeningHoursSpecification(shop),
    telephone: telephone,
    url: url,
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [webPage, entertainmentBusiness],
  };
};

/*
  Blocco EntertainmentBusiness singolo, senza @id, emesso dagli store che non abilitano shopJsonLdGraph.
  Mantenuto identico per non cambiare il JSON-LD degli store esistenti.
*/
const legacyEntertainmentBusiness = (shop: ShopApiDto, name?: string): WithContext<LocalBusiness> => ({
  '@context': 'https://schema.org',
  '@type': 'EntertainmentBusiness',
  address: buildPostalAddress(shop),
  geo: {
    '@type': 'GeoCoordinates',
    latitude: shop.address?.latitude,
    longitude: shop.address?.longitude,
  },
  image: getShopImageUrls(shop),
  name: name,
  telephone: shop.phoneNumber,
  url: window.location.href,
});

export interface EntertainmentBusinessProps {
  shop: ShopApiDto;
}

export const EntertainmentBusiness = ({ shop }: EntertainmentBusinessProps) => {
  const url = useCanonicalUrl();
  const inLanguage = useLanguageTag();
  const name = appConfig.shopNameResolver ? appConfig.shopNameResolver(shop) : shop.name;
  return (
    <Helmet
      script={[
        appConfig.shopJsonLdGraph
          ? jsonLdScriptProp(
              buildEntertainmentBusinessGraph({
                brand: appConfig.shopJsonLdBrandResolver?.(shop),
                inLanguage: inLanguage,
                name: name,
                pageTitle: shop.metaData?.title,
                shop: shop,
                url: url,
              })
            )
          : helmetJsonLdProp<LocalBusiness>(legacyEntertainmentBusiness(shop, name), {
              space: 1,
            }),
      ]}
    />
  );
};
